import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import {
  enqueue,
  peekOutbox,
  utilityEvents as utilityEventsCache,
} from "../local/db";
import { syncEngine } from "../local/sync";
import { subscribeToClassRidesPulled } from "../local/rideCacheEvents";

// Utility events (BLOOD / LAMENESS, card #139) — persisted on the Ride
// server-side, delivered cross-scribe. Every scribe/judge terminal scoring a
// ride reads GET /ride/:rideId/utility-events once when the sheet opens, then
// hears about changes through the 5s change check (card #3200: the class's
// rides are re-pulled when its counter moves, and the pulled ride row carries
// utilityEvents), so a flag raised at one judge position shows up at every
// other position scoring the same ride. The per-ride 4s poll is gone.

/**
 * @param {string} rideId
 * @param {{ online?: boolean }} [opts]
 * @returns {{
 *   events: Array,
 *   error: Error|null,
 *   raise: (type: string) => Promise<void>,
 *   clear: (eventId: string) => Promise<void>,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useRideUtilityEvents(rideId, { online = true } = {}) {
  const scoreApi = useScoreApi();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // Monotonic request-generation counter (review fix, card #139): refresh()
  // can be in flight (poll interval, or a raise()/clear() triggering an
  // extra refresh) when a newer refresh() is issued — e.g. an interval tick
  // that started before a raise() lands can resolve after it, and a blind
  // setEvents(rows) from that stale response would momentarily blank a
  // just-raised flag. Every refresh() call captures the generation at start
  // and discards its own result if a newer call has since been issued.
  const refreshGenRef = useRef(0);

  // Raises whose optimistic entry is on screen but whose op is not yet durable
  // in the outbox (#3068). raise() sets the optimistic entry synchronously and
  // only THEN awaits enqueue(); a refresh() that reads the outbox inside that
  // window would merge nothing and blank the chip until the next refresh. So
  // withPendingRaises unions the outbox with this map, and raise() drops its
  // entry only once enqueue() has resolved (from then on the outbox has it).
  const unqueuedRaisesRef = useRef(new Map());

  // Epoch ms at which this hook's own last raise()/clear() fully settled (flush
  // + the server refresh after it). A class pull (hydrate walk or the 5s change
  // check) that BEGAN before that may carry rows that predate the write; with
  // the 4s poll gone nothing would repair the chip it blanked, so such a pull
  // is answered with one server read of this ride instead of being applied.
  const lastWriteSettledAtRef = useRef(0);

  // Pending outbox merge (card #3056): raise() appends an optimistic _pending
  // entry to React state only — it deliberately never writes it into
  // utilityEventsCache (that would be a second source of truth to reconcile
  // when the POST lands). So EVERY refresh() result — the online fetch, the
  // offline cache read, and the error-fallback cache read — must be unioned
  // with the still-pending raiseUtilityEvent ops for this ride, or the next
  // poll/online-flip refresh overwrites state and the queued flag vanishes
  // from the scribe's banner mid-ride. The outbox itself is the single source
  // of truth: once the POST drains the op, it's gone from peekOutbox() and
  // only the server row remains — except for the brief window where the op
  // is still queued but the server already returned the event (flush in
  // flight), which the dedupe below (match on opId or on a server row of the
  // same type raised since the op was queued) keeps from showing twice.
  const withPendingRaises = useCallback(async (rows, id) => {
    // Snapshot BEFORE the outbox read: an entry deleted after this point had
    // its enqueue() resolve first, so the read below sees its op instead.
    const unqueued = [...unqueuedRaisesRef.current.values()]
      .filter((u) => u.rideId === id)
      .map((u) => u.entry);
    const ops = await peekOutbox();
    const pending = ops.filter(
      (op) =>
        op.type === "raiseUtilityEvent" &&
        op.entityKey === id,
    );
    if (!pending.length && !unqueued.length) return rows;
    const merged = [...rows];
    // Already shown: by id, or (flush in flight) by a server row of the same
    // type raised since the entry was queued. Applies equally to outbox ops
    // and to not-yet-enqueued entries, since a background flush can post an op
    // before raise()'s own enqueue() await has resolved (#3068).
    const isShown = (entryId, type, createdAt) =>
      merged.some(
        (e) =>
          e.id === entryId ||
          (e.type === type &&
            !e._pending &&
            Date.parse(e.raisedAt ?? "") >= Date.parse(createdAt ?? "")),
      );
    for (const op of pending) {
      const type = op.payload?.type;
      if (isShown(op.opId, type, op.createdAt)) continue;
      merged.push({
        id: op.opId,
        type,
        raisedByScribeId: null,
        raisedAt: op.createdAt,
        _pending: true,
      });
    }
    for (const entry of unqueued) {
      if (!isShown(entry.id, entry.type, entry.raisedAt)) merged.push(entry);
    }
    return merged;
  }, []);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      if (onlineRef.current) {
        const rows =
          unwrap(await apiRef.current.rides.listRideUtilityEvents(id)) || [];
        if (isStale()) return;
        // #3056 review round 1 — withPendingRaises is itself an await (it
        // reads the outbox), so a newer refresh() can be issued while it is
        // in flight. Re-check staleness AFTER it resolves, or this older
        // refresh's merge lands last and overwrites the newer result — the
        // exact race refreshGenRef exists to prevent, reintroduced one line
        // later. Every branch below does the same re-check.
        const merged = await withPendingRaises(rows, id);
        if (isStale()) return;
        setEvents(merged);
        setError(null);
        await utilityEventsCache.put({
          rideId: id,
          events: rows,
          updatedAt: new Date().toISOString(),
        });
      } else {
        const cached = await utilityEventsCache.get(id);
        if (isStale()) return;
        const merged = await withPendingRaises(cached?.events ?? [], id);
        if (isStale()) return;
        setEvents(merged);
      }
    } catch (err) {
      if (isStale()) return;
      // Network failed after a possible cache hit — fall back to the last
      // known state rather than blanking the banner.
      const cached = await utilityEventsCache.get(id);
      if (isStale()) return;
      if (cached) {
        const merged = await withPendingRaises(cached.events, id);
        if (isStale()) return;
        setEvents(merged);
        setError(null);
      } else {
        setError(err);
      }
    }
  }, [withPendingRaises]);

  // card #3200 - one refresh when the sheet opens (and when connectivity
  // flips), NO interval. After that a flag raised at another judge position
  // arrives with the class re-pull the 5s change check does when anything in
  // this class changes: the pulled ride row carries `utilityEvents`, so no
  // per-ride request is needed (local/rideCacheEvents.js).
  useEffect(() => {
    if (!rideId) {
      setEvents([]);
      return undefined;
    }
    refresh();
    return undefined;
  }, [rideId, refresh, online]);

  useEffect(
    () =>
      subscribeToClassRidesPulled(async ({ rides: pulled, startedAt }) => {
        const id = rideIdRef.current;
        if (!id) return;
        if (startedAt != null && startedAt < lastWriteSettledAtRef.current) {
          // The pull may predate our own write. The change check has already
          // adopted this counter, so no later pull is coming for it: read this
          // one ride from the server once instead (never blank, never miss
          // another position's flag in the same pull).
          if (pulled.some((r) => r.id === id)) refresh();
          return;
        }
        const row = pulled.find((r) => r.id === id);
        if (!row) return;
        const rows = row.utilityEvents ?? [];
        // Supersedes any refresh still in flight, and (like every branch of
        // refresh()) is re-checked after the outbox merge, which is an await.
        const gen = ++refreshGenRef.current;
        try {
          const merged = await withPendingRaises(rows, id);
          if (gen !== refreshGenRef.current) return;
          setEvents(merged);
          setError(null);
          await utilityEventsCache.put({
            rideId: id,
            events: rows,
            updatedAt: new Date().toISOString(),
          });
        } catch {
          // keep what is painted; the next class re-pull tries again
        }
      }),
    [withPendingRaises, refresh],
  );

  // Raise is a QUEUED write (card #139 AC: survives reload + an offline
  // period) — enqueued into the same IndexedDB outbox the score-save path
  // uses, never a direct fetch. An optimistic local entry renders
  // immediately (even offline); it's replaced by the server row once the op
  // drains and the next refresh() lands.
  const raise = useCallback(
    async (type) => {
      const id = rideIdRef.current;
      if (!id || !type) return;
      // #3596 -- one active alert per ride + type. A second tap while one is
      // already active (on screen, or raised a moment ago and still syncing)
      // adds nothing; the server enforces the same rule for raises that race
      // from two screens.
      const alreadyActive =
        eventsRef.current.some((e) => e.type === type) ||
        [...unqueuedRaisesRef.current.values()].some(
          (u) => u.rideId === id && u.entry.type === type,
        );
      if (alreadyActive) return;
      const opId = `utilityEvent:raise:${id}:${type}:${Date.now()}`;
      const optimistic = {
        id: opId,
        type,
        raisedByScribeId: null,
        raisedAt: new Date().toISOString(),
        _pending: true,
      };
      // Any refresh issued before this point may have read the outbox without
      // this op and can no longer be trusted: bump the generation so it is
      // discarded, and register the entry so a refresh issued from here until
      // enqueue() resolves still carries it (#3068).
      refreshGenRef.current += 1;
      unqueuedRaisesRef.current.set(opId, { rideId: id, entry: optimistic });
      setEvents((prev) => [...prev, optimistic]);
      try {
        await enqueue({
          opId,
          type: "raiseUtilityEvent",
          entityKey: id,
          payload: { rideId: id, type },
        });
      } finally {
        unqueuedRaisesRef.current.delete(opId);
      }
      if (syncEngine.isOnline()) {
        await syncEngine.flush();
        await refresh();
        lastWriteSettledAtRef.current = Date.now();
      } else {
        await syncEngine.refreshPending();
      }
    },
    [refresh],
  );

  // Dismiss (the "clear" op) is likewise queued. Optimistically removed
  // locally. #3596: any staff screen may dismiss, and one dismiss ends the
  // alert everywhere; dismissing an alert that is already gone is a success
  // server-side. If a dismiss never lands, the next refresh() brings it back.
  const clear = useCallback(
    async (eventId) => {
      const id = rideIdRef.current;
      if (!id || !eventId) return;
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      await enqueue({
        opId: `utilityEvent:clear:${id}:${eventId}`,
        type: "clearUtilityEvent",
        entityKey: id,
        payload: { rideId: id, eventId },
      });
      if (syncEngine.isOnline()) {
        await syncEngine.flush();
        await refresh();
        lastWriteSettledAtRef.current = Date.now();
      } else {
        await syncEngine.refreshPending();
      }
    },
    [refresh],
  );

  return { events, error, raise, clear, refresh };
}

export default useRideUtilityEvents;
